import {
  AlignmentType,
  BorderStyle,
  convertInchesToTwip,
  Document,
  LineRuleType,
  Packer,
  PageBreak,
  Paragraph,
  ShadingType,
  TabStopType,
  TextRun,
} from "docx";
import { formatUkDateSafe, isSetDate } from "../../../Helpers/Dates";
import { getProductTypeName, ProductType } from "../../../Types/ProductType";
import { ICriteriaItem } from "../../../Types/Products/ICriteriaItemData";
import { IProductQuestionnaire } from "../../../Types/Products/IProductQuestionnaire";
import {
  AreaExportData,
  EXPORT_COLS,
  ExportFieldVisibility,
  ExportOptions,
  fullExportFieldVisibility,
} from "../types";
import {
  additionalInfoEntries,
  criteriaGradeDisplay,
  developDropdownContent,
  developPersonContent,
  developPicklistContent,
  fieldsInExportOrder,
  findGradeOptionForStoredValue,
  gradeDisplay,
  introductionText,
  tagsString,
} from "./exportHelpers";

const FONT = "Arial";
const COLOR_BLACK = "000000";
const COLOR_NAVY = "24394F";
const COLOR_GREY = "6B7280";
const COLOR_LIGHT_GREY = "F3F4F6";

const SIZE_BODY_HALF_PT = 22;
const SIZE_LABEL_HALF_PT = 22;
const SIZE_CRITERIA_TITLE_HALF_PT = 24;
const SIZE_SECTION_TITLE_HALF_PT = 28;
const SIZE_AREA_TITLE_HALF_PT = 36;

const PAGE_MARGIN_VERTICAL_IN = 0.75;
const PAGE_MARGIN_SIDE_IN = 1.25;
const METADATA_LABEL_TAB_IN = 2.35;
const FIELD_INDENT_IN = 0.2;

const paragraphLeft = {
  alignment: AlignmentType.LEFT,
} as const;

function paragraphSpacing(opts: { before?: number; after?: number; line?: number } = {}) {
  return {
    line: opts.line ?? 360,
    lineRule: LineRuleType.AUTO,
    before: opts.before,
    after: opts.after,
  };
}

function normalizeHex(color: string | undefined, fallback: string): string {
  const c = (color ?? "").trim().replace(/^#/, "");
  return /^[0-9a-fA-F]{6}$/.test(c) ? c.toUpperCase() : fallback;
}

function bodyRun(
  text: string,
  opts?: { bold?: boolean; color?: string; size?: number; shadingFill?: string }
): TextRun {
  return new TextRun({
    text,
    font: FONT,
    size: opts?.size ?? SIZE_BODY_HALF_PT,
    color: opts?.color ?? COLOR_BLACK,
    bold: opts?.bold === true ? true : undefined,
    shading: opts?.shadingFill
      ? { type: ShadingType.CLEAR, fill: opts.shadingFill, color: "auto" }
      : undefined,
  });
}

function sizedBoldRun(
  text: string,
  sizeHalfPt: number,
  color = COLOR_BLACK
): TextRun {
  return new TextRun({
    text,
    font: FONT,
    size: sizeHalfPt,
    color,
    bold: true,
  });
}

function metadataRow(label: string, value: string): Paragraph {
  const display = value.trim().length > 0 ? value : "—";
  return new Paragraph({
    ...paragraphLeft,
    tabStops: [
      {
        type: TabStopType.LEFT,
        position: convertInchesToTwip(METADATA_LABEL_TAB_IN),
      },
    ],
    children: [
      bodyRun(label, { bold: true, color: COLOR_GREY, size: SIZE_LABEL_HALF_PT }),
      bodyRun("\t"),
      bodyRun(display),
    ],
    spacing: paragraphSpacing({ after: 100 }),
  });
}

function splitContentParagraphs(text: string, indentIn = 0): Paragraph[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [
      new Paragraph({
        ...paragraphLeft,
        children: [bodyRun("—")],
        spacing: paragraphSpacing({ after: 160 }),
        indent: indentIn ? { left: convertInchesToTwip(indentIn) } : undefined,
      }),
    ];
  }

  const chunks = trimmed
    .split(/\r?\n/)
    .flatMap((line) => line.split(/\n\s*\n/))
    .map((s) => s.trim())
    .filter(Boolean);

  const parts = chunks.length > 0 ? chunks : [trimmed];
  return parts.map((chunk, index) =>
    new Paragraph({
      ...paragraphLeft,
      children: [bodyRun(chunk)],
      spacing: paragraphSpacing({
        after: index === parts.length - 1 ? 180 : 120,
        line: 400,
      }),
      indent: indentIn ? { left: convertInchesToTwip(indentIn) } : undefined,
    })
  );
}

function fieldBlockForVisibility(
  fieldTitle: string,
  content: string,
  vis: ExportFieldVisibility
): Paragraph[] {
  const showTitle = vis["Field title"];
  const showContent = vis["Field content"];
  if (!showTitle && !showContent) {
    return [];
  }

  const out: Paragraph[] = [];
  if (showTitle) {
    out.push(
      new Paragraph({
        ...paragraphLeft,
        children: [
          bodyRun(fieldTitle, { bold: true, color: COLOR_NAVY, size: SIZE_LABEL_HALF_PT }),
        ],
        spacing: paragraphSpacing({ before: 160, after: 80 }),
        indent: { left: convertInchesToTwip(FIELD_INDENT_IN) },
      })
    );
  }
  if (showContent) {
    out.push(
      ...splitContentParagraphs(
        content,
        showTitle ? FIELD_INDENT_IN + 0.1 : FIELD_INDENT_IN
      )
    );
  }
  return out;
}

function criteriaHeadingParagraph(criteriaTitle: string): Paragraph {
  const title =
    criteriaTitle.length > 0 ? criteriaTitle : "Untitled criteria";

  const children: TextRun[] = [
    sizedBoldRun(title, SIZE_CRITERIA_TITLE_HALF_PT, COLOR_NAVY),
  ];

  return new Paragraph({
    ...paragraphLeft,
    children,
    spacing: paragraphSpacing({ before: 120, after: 160 }),
    shading: {
      type: ShadingType.CLEAR,
      fill: COLOR_LIGHT_GREY,
      color: "auto",
    },
    border: {
      left: {
        color: COLOR_NAVY,
        style: BorderStyle.SINGLE,
        size: 24,
        space: 4,
      },
    },
  });
}

function sectionHeadingParagraph(sectionTitle: string): Paragraph {
  const label = sectionTitle.length > 0 ? sectionTitle : "Untitled";
  return new Paragraph({
    ...paragraphLeft,
    children: [
      sizedBoldRun(`Section: ${label}`, SIZE_SECTION_TITLE_HALF_PT, "FFFFFF"),
    ],
    spacing: paragraphSpacing({ before: 360, after: 240 }),
    shading: {
      type: ShadingType.CLEAR,
      fill: "4B5563",
      color: "auto",
    },
  });
}

function criteriaDetailRow(label: string, value: string): Paragraph {
  const display = value.trim().length > 0 ? value : "—";
  return new Paragraph({
    ...paragraphLeft,
    tabStops: [
      {
        type: TabStopType.LEFT,
        position: convertInchesToTwip(2.1),
      },
    ],
    children: [
      bodyRun(label, { bold: true, color: COLOR_GREY, size: SIZE_LABEL_HALF_PT }),
      bodyRun("\t"),
      bodyRun(display),
    ],
    spacing: paragraphSpacing({ after: 90 }),
    indent: { left: convertInchesToTwip(0.35) },
  });
}

function buildAreaParagraphs(
  area: AreaExportData,
  vis: ExportFieldVisibility
): Paragraph[] {
  const out: Paragraph[] = [];
  const q = area.questionnaire;
  const title = q.title ?? "";
  const templateType = getProductTypeName(q.productType);
  const overallGrade = gradeDisplay(q);
  const due = formatUkDateSafe(area.displayInfo?.dueDate);
  const owner = area.displayInfo?.ownerName ?? "";
  const completionRaw = area.displayInfo?.completedDate;
  const completion = formatUkDateSafe(
    isSetDate(completionRaw) ? completionRaw : undefined
  );
  const isDevelop = q.productType === ProductType.Develop;
  const personTitle = (q.personName?.trim() || "Person") as string;
  const picklistTitle = (q.picklistName?.trim() || "Picklist") as string;
  const dropdownTitle = (q.dropdownName?.trim() || "Dropdown") as string;

  if (vis.Title) {
    out.push(
      new Paragraph({
        ...paragraphLeft,
        children: [
          sizedBoldRun(
            title.length > 0 ? title : "Untitled",
            SIZE_AREA_TITLE_HALF_PT,
            COLOR_NAVY
          ),
        ],
        spacing: paragraphSpacing({ after: 280 }),
        border: {
          bottom: {
            color: COLOR_NAVY,
            style: BorderStyle.SINGLE,
            size: 16,
            space: 6,
          },
        },
      })
    );
  }

  if (vis.Grade && overallGrade !== "Not Set") {
    out.push(metadataRow("Overall grade", overallGrade));
  }
  if (vis["Due date"]) {
    out.push(metadataRow("Due date", due));
  }
  if (vis.Owner) {
    out.push(metadataRow("Owner", owner));
  }
  if (vis["Type of template"]) {
    out.push(metadataRow("Type of template", templateType));
  }
  if (vis["Completion date"]) {
    out.push(metadataRow("Completion date", completion));
  }

  if (
    vis.Grade ||
    vis["Due date"] ||
    vis.Owner ||
    vis["Type of template"] ||
    vis["Completion date"]
  ) {
    out.push(
      new Paragraph({
        spacing: paragraphSpacing({ after: 200 }),
      })
    );
  }

  if (vis.Introduction) {
    const intro = introductionText(q);
    if (intro) {
      out.push(
        new Paragraph({
          ...paragraphLeft,
          children: [
            sizedBoldRun("Introduction", SIZE_SECTION_TITLE_HALF_PT, COLOR_NAVY),
          ],
          spacing: paragraphSpacing({ before: 200, after: 160 }),
        })
      );
      out.push(...splitContentParagraphs(intro));
    }
  }

  if (vis["Additional information"]) {
    const entries = additionalInfoEntries(q);
    if (entries.length > 0) {
      out.push(
        new Paragraph({
          ...paragraphLeft,
          children: [
            sizedBoldRun(
              "Additional information",
              SIZE_SECTION_TITLE_HALF_PT,
              COLOR_NAVY
            ),
          ],
          spacing: paragraphSpacing({ before: 280, after: 160 }),
        })
      );
      for (const entry of entries) {
        out.push(metadataRow(entry.header || "(No title)", entry.value));
      }
      out.push(
        new Paragraph({
          spacing: paragraphSpacing({ after: 200 }),
        })
      );
    }
  }

  for (const section of q.questionnaireSections ?? []) {
    const sectionTitle = section.title ?? "";
    if (vis.Section) {
      out.push(sectionHeadingParagraph(sectionTitle));
    }

    for (const c of section.criteria ?? []) {
      const criteriaTitle = c.title ?? "";
      if (vis.Criteria) {
        out.push(criteriaHeadingParagraph(criteriaTitle));
      }

      const tags = tagsString(c);
      const evidence = (area.evidenceByCriteriaId[c.id] ?? []).join("; ");
      const fields = fieldsInExportOrder(c.fields);

      for (const f of fields) {
        const fieldTitle = f.title
          ? `${f.title}${f.mandatory ? " *" : ""}`
          : "";
        const content = f.content ?? "";
        if (!fieldTitle && !content.trim()) continue;
        out.push(
          ...fieldBlockForVisibility(
            fieldTitle.length > 0 ? fieldTitle : "(No title)",
            content,
            vis
          )
        );
      }

      if (isDevelop) {
        if (q.personShown) {
          out.push(
            ...fieldBlockForVisibility(
              personTitle,
              developPersonContent(c),
              vis
            )
          );
        }
        if (q.dropdownShown) {
          out.push(
            ...fieldBlockForVisibility(
              dropdownTitle,
              developDropdownContent(c),
              vis
            )
          );
        }
        if (q.picklistShown) {
          out.push(
            ...fieldBlockForVisibility(
              picklistTitle,
              developPicklistContent(c),
              vis
            )
          );
        }
      }

      if (vis.Grade) {
        out.push(criteriaDetailRow("Grade", criteriaGradeDisplay(q, c)));
      }

      if (vis.Tags && tags.length > 0) {
        out.push(criteriaDetailRow("Tags", tags));
      }

      if (vis["Linked evidence files"] && evidence.length > 0) {
        out.push(criteriaDetailRow("Linked evidence files", evidence));
      }

      out.push(
        new Paragraph({
          spacing: paragraphSpacing({ after: 240 }),
        })
      );
    }
  }

  return out;
}

export async function docxExporter(
  areas: AreaExportData[],
  _baseFilename: string,
  options?: ExportOptions
): Promise<Blob> {
  if (areas.length === 0) {
    throw new Error("No areas to export.");
  }

  const vis = fullExportFieldVisibility(options?.fieldVisibility);
  if (!EXPORT_COLS.some((k) => vis[k] === true)) {
    throw new Error("No export columns selected.");
  }

  const children: Paragraph[] = [];
  areas.forEach((area, index) => {
    if (index > 0) {
      children.push(
        new Paragraph({
          spacing: paragraphSpacing(),
          children: [new PageBreak()],
        })
      );
    }
    children.push(...buildAreaParagraphs(area, vis));
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(PAGE_MARGIN_VERTICAL_IN),
              right: convertInchesToTwip(PAGE_MARGIN_SIDE_IN),
              bottom: convertInchesToTwip(PAGE_MARGIN_VERTICAL_IN),
              left: convertInchesToTwip(PAGE_MARGIN_SIDE_IN),
            },
          },
        },
        children,
      },
    ],
  });

  return Packer.toBlob(doc);
}
